// src/App.js
import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@vercel/analytics/react";
import HomePage from "./pages/HomePage";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import SEO from "./components/SEO";
import AdminRoute from "./components/AdminRoute";
import SiteFooter from "./components/SiteFooter";
import "./styles/MobileEnhancements.css";
import "./styles/EnterpriseOverrides.css";
import "./styles/SiteFooter.css";

const RestaurantPage = lazy(() => import("./pages/RestaurantPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const LeaderboardsPage = lazy(() => import("./pages/LeaderboardsPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ReviewSubmissionPage = lazy(() => import("./pages/ReviewSubmissionPage"));
const ReceiptScanLanding = lazy(() => import("./pages/ReceiptScanLanding"));
const ReviewSuccessPage = lazy(() => import("./pages/ReviewSuccessPage"));
const AddRestaurantPage = lazy(() => import("./pages/AddRestaurantPage"));
const ScoringCriteriaPage = lazy(() => import("./pages/ScoringCriteriaPage"));
const MapRatingsPage = lazy(() => import("./pages/MapRatingsPage"));
const SocialUploadPage = lazy(() => import("./pages/SocialUploadPage"));
const PlacesAdminPage = lazy(() => import("./pages/PlacesAdminPage"));
const SeoDashboardPage = lazy(() => import("./pages/SeoDashboardPage"));
const MenuImportAdminPage = lazy(() => import("./pages/MenuImportAdminPage"));
const BadgePage = lazy(() => import("./pages/BadgePage"));

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <div className="App no-overflow">
          <SEO />
          <ScrollToTop />
          <Navbar />
          <main className="main-content no-overflow">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />

                {/* City-specific routes for SEO */}
                <Route path="/city/:cityName" element={<HomePage />} />
                <Route path="/city/:cityName/:province" element={<HomePage />} />
                <Route
                  path="/city/:cityName/:province/:country"
                  element={<HomePage />}
                />

                {/* Leaderboard city routes */}
                <Route path="/best/:typeSlug" element={<LeaderboardsPage />} />
                <Route
                  path="/leaderboards/:cityName/:province/:country/:typeSlug"
                  element={<LeaderboardsPage />}
                />
                <Route
                  path="/leaderboards/:cityName"
                  element={<LeaderboardsPage />}
                />
                <Route
                  path="/leaderboards/:cityName/:province"
                  element={<LeaderboardsPage />}
                />
                <Route
                  path="/leaderboards/:cityName/:province/:country"
                  element={<LeaderboardsPage />}
                />

                {/* Existing routes */}
                <Route
                  path="/restaurant/:restaurantID"
                  element={<RestaurantPage />}
                />
                <Route path="/badge/:restaurantSlug" element={<BadgePage />} />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/feed" element={<FeedPage />} />
                <Route path="/users/:id" element={<ProfilePage />} />
                <Route path="/leaderboards" element={<LeaderboardsPage />} />
                <Route path="/map" element={<MapRatingsPage />} />
                <Route
                  path="/scoring-criteria"
                  element={<ScoringCriteriaPage />}
                />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route
                  path="/submit-review/scan"
                  element={
                    <ProtectedRoute>
                      <ReceiptScanLanding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submit-review"
                  element={
                    <ProtectedRoute>
                      <ReviewSubmissionPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/review-success" element={<ReviewSuccessPage />} />
                <Route
                  path="/reset-password/:token"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="/add-restaurant"
                  element={
                    <ProtectedRoute>
                      <AddRestaurantPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/social"
                  element={
                    <AdminRoute>
                      <SocialUploadPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/places"
                  element={
                    <AdminRoute>
                      <PlacesAdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/seo"
                  element={
                    <AdminRoute>
                      <SeoDashboardPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/menus"
                  element={
                    <AdminRoute>
                      <MenuImportAdminPage />
                    </AdminRoute>
                  }
                />
              </Routes>
            </Suspense>
          </main>
          <SiteFooter />
          <Analytics />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
